import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { ErrorMessage, Form, Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { TagsInput } from 'react-tag-input-component'
import { useImmer } from 'use-immer'
import FormikControl from '../../components/FormikControl'
import Loader from '../../components/Loader'
import ReactSelect from '../../components/ReactSelect'
import TextError from '../../components/TextError'
import CustomToast from '../../components/Toast/CustomToast'
import {
  arrangeNamesBySequence,
  changeHandler,
  decodeId,
  encodeId,
  prepareNotificationData,
  showToast
} from '../../lib/AllGlobalFunction'
import {
  isAllowPriceVariant,
  isAllowWarehouseManagement,
  isInventoryModel,
  isSellerPanel,
  productStatus
} from '../../lib/AllStaticVariables'
import axiosProvider from '../../lib/AxiosProvider'
import { _exception } from '../../lib/exceptionMessage'
import { setPageTitle } from '../redux/slice/pageTitleSlice'
import ImagesAndLink from './ImagesAndLink'
import NotWarehouseAndPrice from './NotWarehouseAndPrice'
import NotWarehousePrice from './NotWarehousePrice'
import Specifications from './Specifications'
import WarehouseAndPrice from './WarehouseAndPrice'
import WarehouseNotPrice from './WarehouseNotPrice'
import {
  fetchEditData,
  fetchExtraData,
  getHsnCodeOptions,
  getMatchingHsnCode,
  validateForm
} from './productUtils/helperFunctions'
import { productInitVal, productValidationSchema } from './productUtils/init'
import { prepareProductPayload } from './productUtils/prepareProductPayload'
import {
  checkAndSetSKUCode,
  handleBrandChange,
  handleCategoryChange,
  handleSellerChange
} from './productUtils/productFunctions'

const AddProduct = () => {
  const [loading, setLoading] = useState(false)
  const [calculation, setCalculation] = useState()
  const [scrollPosition, setScrollPosition] = useState(true)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { userInfo, sellerDetails } = useSelector((state) => state?.user)
  const encodedId = searchParams.get('id')
  const encodeSellerId = searchParams.get('sellerId')
  const id = encodedId ? decodeId(encodedId) : null
  const sellerId = encodeSellerId ? decodeId(encodeSellerId) : null
  const lastScrollPosition = useRef(0)
  const [modalShow, setModalShow] = useState(false)
  const [initialValues, setInitialValues] = useState({
    ...productInitVal,
    sellerID:
      isInventoryModel || isSellerPanel
        ? isSellerPanel
          ? userInfo?.userId
          : sellerDetails?.userID
        : null,
    sellerName:
      isInventoryModel || isSellerPanel
        ? isSellerPanel
          ? userInfo?.userId
          : sellerDetails?.displayName
        : ''
  })
  const [allState, setAllState] = useImmer({
    sellerDetails: [],
    brand: [],
    category: [],
    color: [],
    sizeType: [],
    warehouseDetails: [],
    hsnCode: [],
    weight: []
  })
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })
  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    if (id) {
      fetchEditData({
        setAllState,
        allState,
        setLoading,
        id,
        sellerId,
        initialValues,
        setInitialValues,
        setCalculation,
        calculation,
        toast,
        setToast,
        navigate
      })
    } else {
      fetchExtraData({
        allState,
        setAllState,
        sellerDetails,
        setCalculation,
        calculation,
        setLoading
      })
    }
  }, [])

  useEffect(() => {
    function handleScroll() {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop

      if (currentScrollPosition > 100) {
        if (currentScrollPosition > lastScrollPosition.current) {
          setScrollPosition(false)
        } else {
          setScrollPosition(true)
        }
      } else {
        setScrollPosition(false)
      }

      lastScrollPosition.current = currentScrollPosition
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleSubmit = async (values) => {
    if (!values?.productPrices?.length) {
      showToast(toast, setToast, {
        code: 204,
        message:
          'Unexpected error while saving the data. Please try again later.'
      })
      return
    }

    try {
      setLoading(true)
      const response = await axiosProvider({
        method: values?.productId ? 'PUT' : 'POST',
        endpoint: 'Product/Product',
        data: prepareProductPayload(values),
        oldData: initialValues,
        location: location?.pathname,
        userId: userInfo?.userId
      })
      setLoading(false)

      if (response?.data?.code === 200) {
        const productID = values?.productId
          ? values?.productId
          : response?.data?.data

        const notificationData = prepareNotificationData({
          imageUrl: values?.productImage[0]?.image,
          reciverId: userInfo?.userId,
          userId: userInfo?.userId,
          userType: userInfo?.userType,
          notificationTitle: `Product name: ${values?.productName} ${
            values?.productId ? 'updated successfully' : 'added successfully'
          }`,
          notificationDescription: `${
            values?.productId ? 'Product added by:' : 'Product updated by:'
          } ${userInfo?.fullName}`,
          url: `/manage-product?id=${encodeId(productID)}`,
          notifcationsof: 'Product'
        })

        await axiosProvider({
          endpoint: 'Notification',
          method: 'POST',
          data: notificationData
        })

        setToast({
          show: true,
          text: response?.data?.message,
          variation: response?.data?.code !== 200 ? 'error' : 'success'
        })

        setTimeout(() => {
          navigate('/manage-product')
          setToast({ ...toast, show: false })
        }, 2000)
      } else {
        showToast(toast, setToast, response)
      }
    } catch {
      setLoading(false)
      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  useEffect(() => {
    dispatch(setPageTitle(id ? 'Edit Product' : 'Add Product'))
  }, [])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={productValidationSchema(initialValues, allState)}
    >
      {({
        values,
        setFieldValue,
        resetForm,
        setTouched,
        setErrors,
        errors,
        touched
      }) => (
        <Form className='product_form'>
          {console.log(values)}
          <div
            className={
              !scrollPosition
                ? 'card position-sticky top_80 active'
                : 'card position-sticky top_80'
            }
          >
            <div className='card-body'>
              <div className=' d-flex justify-content-between align-items-center'>
                <div>
                  {!scrollPosition && (
                    <label className='form-label fw-normal text-nowrap mb-0'>
                      Product Name
                    </label>
                  )}

                  <p
                    className='mb-0 bold text-nowrap'
                    style={{ fontSize: '22px', color: '#001B5A' }}
                  >
                    {arrangeNamesBySequence(values?.productName ?? [])}
                  </p>
                  {values?.productName?.length <= 0 && (
                    <p
                      className='mb-0 bold text-nowrap'
                      style={{ fontSize: '14px' }}
                    >
                      Please fill in all details to generate the product name.
                    </p>
                  )}
                </div>
                <div className='d-flex align-items-center gap-4 w-100 justify-content-end'>
                  <div className='col-md-4 col-xxl-3'>
                    <div className='input-file-wrapper'>
                      {!scrollPosition && (
                        <label className='form-label fw-normal required'>
                          Product Status
                        </label>
                      )}
                      <ReactSelect
                        id='status'
                        name='status'
                        isRequired
                        errors={errors?.status}
                        touched={touched?.status ?? false}
                        placeholder='Product Status'
                        options={productStatus}
                        value={
                          values?.status && {
                            value: values?.status,
                            label: values?.status
                          }
                        }
                        onChange={(e) => {
                          setFieldValue('status', e?.value ?? '')
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    {!scrollPosition && (
                      <label className='form-label fw-normal'>
                        Is Product Live
                      </label>
                    )}

                    <div className='switch'>
                      <input
                        type='radio'
                        value={true}
                        id='yes'
                        checked={values?.live}
                        name='choice'
                        onChange={(e) => {
                          if (e?.target?.checked) setFieldValue('live', true)
                        }}
                      />
                      <label htmlFor='yes'>Yes</label>
                      <input
                        type='radio'
                        value={false}
                        id='no'
                        name='choice'
                        checked={!values?.live}
                        onChange={(e) => {
                          if (e?.target?.checked) setFieldValue('live', false)
                        }}
                      />
                      <label htmlFor='no'>No</label>
                      <span className='switchFilter'></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {toast?.show && (
            <CustomToast text={toast?.text} variation={toast?.variation} />
          )}

          {loading && <Loader />}

          <div className='card'>
            <div className='card-body'>
              {!id && <h5 className='mb-3 head_h3'>Add New Product</h5>}

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      id='customeProductName'
                      control='input'
                      type='text'
                      label='Custom Product Name'
                      name='customeProductName'
                      placeholder='Custom Product Name'
                      onChange={(e) => {
                        changeHandler(
                          'customeProductName',
                          e?.target?.value,
                          setFieldValue
                        )
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>

                <div className='col-md-3'>
                  <div className='input-file-wrapper mb-3 position-relative'>
                    <FormikControl
                      id='companySKUCode'
                      control='input'
                      type='text'
                      isRequired
                      name='companySKUCode'
                      label='Product SKU'
                      placeholder='Product SKU'
                      onChange={(e) => {
                        changeHandler(
                          'companySKUCode',
                          e?.target?.value,
                          setFieldValue
                        )
                      }}
                      onBlur={async () => {
                        checkAndSetSKUCode(true, values, resetForm)
                      }}
                    />
                    {values?.isCompanySKUAvailable && (
                      <i className='m-icon m-icon--tick-icon kl-verify-icon'></i>
                    )}
                    {values?.companySKUCode && (
                      <ErrorMessage
                        name='isCompanySKUAvailable'
                        component={TextError}
                      />
                    )}
                  </div>
                </div>

                <div className='col-md-3'>
                  <div className='input-file-wrapper mb-3 position-relative'>
                    <FormikControl
                      id='sellerSKU'
                      control='input'
                      type='text'
                      isRequired
                      label='Seller SKU code'
                      name='sellerSKU'
                      placeholder='Seller SKU code'
                      onBlur={async () => {
                        checkAndSetSKUCode(false, values, resetForm)
                      }}
                      onChange={(e) => {
                        changeHandler(
                          'sellerSKU',
                          e?.target?.value,
                          setFieldValue
                        )
                      }}
                    />
                    {values?.isSellerSKUAvailable && (
                      <i className='m-icon m-icon--tick-icon kl-verify-icon'></i>
                    )}
                    {values?.sellerSKU && (
                      <ErrorMessage
                        name='isSellerSKUAvailable'
                        component={TextError}
                      />
                    )}
                  </div>
                </div>

                {!isInventoryModel && (
                  <div className='col-md-3'>
                    <div className='input-file-wrapper mb-3'>
                      <label className='form-label required'>
                        Select Seller
                      </label>
                      <ReactSelect
                        id='sellerID'
                        name='sellerID'
                        isRequired
                        errors={errors?.sellerID}
                        touched={touched?.sellerID ?? false}
                        placeholder='Select Seller'
                        isDisabled={values?.productId ? true : false}
                        value={
                          values?.sellerID && {
                            value: values?.sellerID,
                            label: values?.sellerName
                          }
                        }
                        options={allState?.sellerDetails?.map(
                          ({
                            userId,
                            displayName,
                            shipmentBy,
                            shipmentChargesPaidByName
                          }) => ({
                            value: userId,
                            label: displayName,
                            shipmentBy,
                            shipmentPaidBy: shipmentChargesPaidByName
                          })
                        )}
                        onChange={(e) => {
                          setFieldValue('sellerID', sellerDetails?.sellerID)
                          setFieldValue('sellerName', sellerDetails?.sellerName)
                          setFieldValue('shipmentBy', sellerDetails?.shipmentBy)
                          setFieldValue(
                            'shipmentPaidBy',
                            sellerDetails?.shipmentPaidBy
                          )

                          handleSellerChange({
                            sellerDetails: {
                              sellerID: e?.value,
                              sellerName: e?.label,
                              shipmentBy: e?.shipmentBy,
                              shipmentPaidBy: e?.shipmentPaidBy
                            },
                            values,
                            resetForm,
                            allState,
                            setAllState,
                            navigate,
                            setCalculation,
                            calculation,
                            setFieldValue,
                            setLoading
                          })
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className='col-md-3'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>Select Brand</label>
                    <ReactSelect
                      id='brandID'
                      name='brandID'
                      isRequired
                      errors={errors?.brandID}
                      touched={touched?.brandID ?? false}
                      placeholder='Select Brand'
                      value={
                        values?.brandID && {
                          value: values?.brandID,
                          label: values?.brandName
                        }
                      }
                      isDisabled={values?.productId ? true : false}
                      options={allState?.brand?.map(
                        ({ brandId, brandName }) => ({
                          value: brandId,
                          label: brandName
                        })
                      )}
                      onChange={async (e) => {
                        handleBrandChange({
                          brand: {
                            brandID: e?.value,
                            brandName: e?.label
                          },
                          values,
                          resetForm,
                          setAllState,
                          setCalculation,
                          calculation
                        })
                      }}
                    />
                  </div>
                </div>

                <div className={isInventoryModel ? 'col-md-9' : 'col-md-6'}>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>
                      Select Category
                    </label>
                    <ReactSelect
                      id='categoryId'
                      name='categoryId'
                      isRequired
                      errors={errors?.categoryId}
                      touched={touched?.categoryId ?? false}
                      placeholder='Select Category'
                      value={
                        values?.categoryId && {
                          value: values?.categoryId,
                          label: values?.categoryPathName
                        }
                      }
                      isDisabled={values?.productId ? true : false}
                      options={allState?.category?.map(
                        ({ id, pathNames, name }) => ({
                          value: id,
                          label: pathNames,
                          categoryName: name
                        })
                      )}
                      onChange={async (e) => {
                        setFieldValue('categoryId', e?.value ?? null)
                        setFieldValue('categoryName', e?.categoryName ?? null)
                        setFieldValue('categoryPathName', e?.label ?? null)

                        handleCategoryChange({
                          category: {
                            categoryPathName: e?.label,
                            categoryName: e?.categoryName,
                            categoryId: e?.value
                          },
                          values,
                          setFieldValue,
                          resetForm,
                          setAllState,
                          navigate,
                          setLoading,
                          setCalculation,
                          calculation
                        })
                      }}
                    />
                  </div>
                </div>

                <div className='mb-3 col-md-12' id='description'>
                  <label className='form-label required'>Description</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={values?.description ? values?.description : '<p></p>'}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      setFieldValue('description', data)
                    }}
                    onReady={(editor) => {
                      setFieldValue('description-editor', editor)
                    }}
                    onBlur={(editor) => {
                      if (!values['description-editor']) {
                        setFieldValue('description-editor', editor)
                      }
                    }}
                    config={{
                      toolbar: ['|', 'bold', 'italic', '|', 'undo', 'redo']
                    }}
                  />
                  <ErrorMessage name='description' component={TextError} />
                </div>

                <div className='mb-3 col-md-12'>
                  <label className='form-label'>Highlights</label>
                  <CKEditor
                    id='highlights'
                    editor={ClassicEditor}
                    data={values?.highlights ? values?.highlights : '<p></p>'}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      setFieldValue('highlights', data)
                    }}
                    onReady={(editor) => {
                      setFieldValue('highlights-editor', editor)
                    }}
                    onBlur={(editor) => {
                      if (!values['highlights-editor']) {
                        setFieldValue('highlights-editor', editor)
                      }
                    }}
                    config={{
                      toolbar: [
                        '|',
                        'bold',
                        'italic',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo'
                      ]
                    }}
                  />
                </div>

                <div className='col-md-6'>
                  <label className='form-label'>Keywords</label>
                  <div className='input-file-wrapper mb-3'>
                    <TagsInput
                      value={values?.keywords ?? []}
                      separators={['Enter', ',']}
                      placeHolder='Keywords'
                      onChange={(tags) => {
                        setFieldValue('keywords', tags)
                      }}
                      name='keywords'
                    />
                  </div>
                </div>

                <div className='col-md-4 col-xxl-3'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>
                      Select HSN Code
                    </label>
                    <ReactSelect
                      id='hsnCodeId'
                      name='hsnCodeId'
                      isRequired
                      errors={errors?.hsnCodeId}
                      touched={touched?.hsnCodeId}
                      placeholder='Select HSN Code'
                      value={
                        values?.hsnCodeId &&
                        getMatchingHsnCode(values, allState?.hsnCode)
                      }
                      options={getHsnCodeOptions(allState?.hsnCode)}
                      onChange={(e) => {
                        setFieldValue('hsnCodeId', e?.hsnCodeId ?? '')
                        setFieldValue('taxValueId', e?.taxValueId ?? '')
                      }}
                    />
                  </div>
                </div>

                <div className='col-md-5'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='Meta Title'
                      id='metaTitle'
                      type='text'
                      name='metaTitle'
                      onChange={(e) => {
                        changeHandler(
                          'metaTitle',
                          e?.target?.value,
                          setFieldValue
                        )
                      }}
                      placeholder='Meta Title'
                    />
                  </div>
                </div>

                <div className='col-md-7'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='Meta Description'
                      id='metaDescription'
                      type='text'
                      name='metaDescription'
                      onChange={(e) => {
                        changeHandler(
                          'metaDescription',
                          e?.target?.value,
                          setFieldValue
                        )
                      }}
                      placeholder='Meta Description'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {values?.sellerID && values?.brandID && values?.categoryId && (
            <>
              {allState?.specificationData?.length > 0 && (
                <Specifications
                  specificationData={allState?.specificationData}
                  values={values}
                  setFieldValue={setFieldValue}
                  resetForm={resetForm}
                  errors={errors}
                  touched={touched}
                />
              )}

              {!isAllowWarehouseManagement && !isAllowPriceVariant && (
                <NotWarehouseAndPrice
                  allState={allState}
                  values={values}
                  setFieldValue={setFieldValue}
                  calculation={calculation}
                  setCalculation={setCalculation}
                  resetForm={resetForm}
                  errors={errors}
                  touched={touched}
                />
              )}

              {isAllowWarehouseManagement && !isAllowPriceVariant && (
                <WarehouseNotPrice
                  allState={allState}
                  values={values}
                  setFieldValue={setFieldValue}
                  calculation={calculation}
                  setCalculation={setCalculation}
                  resetForm={resetForm}
                  errors={errors}
                  touched={touched}
                />
              )}

              {!isAllowWarehouseManagement && isAllowPriceVariant && (
                <NotWarehousePrice
                  allState={allState}
                  values={values}
                  setFieldValue={setFieldValue}
                  calculation={calculation}
                  setCalculation={setCalculation}
                  resetForm={resetForm}
                  errors={errors}
                  touched={touched}
                />
              )}

              {isAllowWarehouseManagement && isAllowPriceVariant && (
                <WarehouseAndPrice
                  allState={allState}
                  values={values}
                  setFieldValue={setFieldValue}
                  calculation={calculation}
                  setCalculation={setCalculation}
                  resetForm={resetForm}
                  errors={errors}
                  touched={touched}
                />
              )}

              {values?.productName?.length > 0 && (
                <ImagesAndLink
                  values={values}
                  setFieldValue={setFieldValue}
                  resetForm={resetForm}
                  setModalShow={setModalShow}
                  modalShow={modalShow}
                />
              )}

              <Button
                variant='primary'
                className='d-flex align-items-center gap-2 justify-content-center fw-semibold'
                onClick={() => {
                  validateForm({
                    values,
                    setErrors,
                    setTouched,
                    resetForm,
                    errors,
                    toast,
                    setToast,
                    handleSubmit,
                    allState
                  })
                }}
              >
                {values?.productId ? 'Update product' : 'Save product'}
              </Button>
            </>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default AddProduct
